/**
 * Timeline Transformers
 *
 * Transforms API responses into UI-friendly timeline format.
 * Frontend handles all display logic (titles, descriptions, icons, variants).
 * Backend only provides status and event_type for decision making.
 */

import type { TimelineEvent } from '../types/timeline';
import type {
  TimelineEventAPI,
  DataSessionAPI,
  ConsentTimelineAPIResponse,
  TransformedDataSession,
} from '../types/consent';

/**
 * Event display configuration mapping
 */
interface EventDisplayConfig {
  title: string;
  description: string;
  icon: string;
  variant: 'success' | 'warning' | 'error' | 'info';
}

function getEventDisplayConfig(eventType: string, status: string): EventDisplayConfig {
  const configs: Record<string, EventDisplayConfig> = {
    // Consent events
    CONSENT_REQUESTED: {
      title: 'Consent requested by FIU',
      description: 'FIU initiated consent request',
      icon: 'request',
      variant: 'info',
    },
    CONSENT_APPROVED: {
      title: 'Consent acted on by user',
      description: 'User approved the consent request',
      icon: 'checkCircle',
      variant: 'success',
    },
    CONSENT_REJECTED: {
      title: 'Consent rejected by user',
      description: 'User rejected the consent request',
      icon: 'xCircle',
      variant: 'error',
    },
    CONSENT_PAUSED: {
      title: 'Consent paused by user',
      description: 'User paused the consent',
      icon: 'pause',
      variant: 'warning',
    },
    CONSENT_REVOKED: {
      title: 'Consent revoked by user',
      description: 'User revoked the consent',
      icon: 'xCircle',
      variant: 'error',
    },
    WEBHOOK_CONSENT_NOTIFICATION_SENT: {
      title: 'Consent notification sent to FIU',
      description: 'Notification forwarded to FIU',
      icon: 'send',
      variant: 'info',
    },

    // Data events
    DATA_REQUESTED: {
      title: 'FIU requests data',
      description: 'Data request initiated by FIU',
      icon: 'dataRequest',
      variant: 'info',
    },
    FI_NOTIFICATION_RECEIVED: {
      title: 'Notification received from AA',
      description: 'Account Aggregator sent notification',
      icon: 'notification',
      variant: 'info',
    },
    DATA_READY: {
      title: 'Data received from AA',
      description: 'Data is ready for fetch',
      icon: 'checkCircle',
      variant: 'success',
    },
    DATA_DELIVERED: {
      title: 'Data received from AA',
      description: 'Data successfully delivered',
      icon: 'checkCircle',
      variant: 'success',
    },
    FI_NOTIFICATION_FAILED: {
      title: 'Notification failed',
      description: 'FI notification failed',
      icon: 'xCircle',
      variant: 'error',
    },
    DATA_FETCH_COMPLETE: {
      title: 'Data stored successfully',
      description: 'Data fetch completed',
      icon: 'download',
      variant: 'success',
    },
    WEBHOOK_DATA_NOTIFICATION_SENT: {
      title: 'Notification sent to FIU',
      description: 'Data notification forwarded to FIU',
      icon: 'send',
      variant: 'info',
    },
  };

  // Handle webhook events with dynamic status
  if (eventType.startsWith('WEBHOOK_')) {
    const webhookConfig = configs[eventType] || {
      title: eventType.replace(/^WEBHOOK_/, '').replace(/_/g, ' '),
      description: 'Webhook event',
      icon: 'send',
      variant: 'info' as const,
    };

    // Override variant based on webhook status
    if (status === 'SUCCESS') {
      return { ...webhookConfig, variant: 'success' };
    } else if (status === 'FAILED') {
      return { ...webhookConfig, variant: 'error' };
    }
    return webhookConfig;
  }

  // Return config or generate default
  return (
    configs[eventType] || {
      title: eventType.replace(/_/g, ' '),
      description: status,
      icon: 'info',
      variant: 'info',
    }
  );
}

/**
 * Get status badge color based on event_type and status
 *
 * Angular behavior: Badge color is derived from the event meaning, not the raw status.
 * - Consent events: Use event_type to determine badge (APPROVED = green, REJECTED = red)
 * - Webhook events: Use status field (SUCCESS = green, FAILED = red)
 * - Data events: Use event_type to determine badge
 */
function getStatusBadgeType(status: string, eventType?: string): 'success' | 'warning' | 'error' | 'info' | 'pending' {
  const eventTypeUpper = (eventType || '').toUpperCase();
  const statusUpper = status.toUpperCase();

  // For consent events, derive badge from event_type (matching Angular behavior)
  if (eventTypeUpper === 'CONSENT_APPROVED') {
    return 'success'; // Green - consent was approved
  }
  if (eventTypeUpper === 'CONSENT_REJECTED' || eventTypeUpper === 'CONSENT_REVOKED') {
    return 'error'; // Red - consent was rejected/revoked
  }
  if (eventTypeUpper === 'CONSENT_PAUSED') {
    return 'warning'; // Orange/Yellow - consent was paused
  }
  if (eventTypeUpper === 'CONSENT_REQUESTED') {
    return 'pending'; // Blue - consent is pending
  }

  // For data events, derive badge from event_type
  if (eventTypeUpper === 'DATA_READY' || eventTypeUpper === 'DATA_DELIVERED' || eventTypeUpper === 'DATA_FETCH_COMPLETE') {
    return 'success'; // Green - data operation successful
  }
  if (eventTypeUpper === 'FI_NOTIFICATION_FAILED') {
    return 'error'; // Red - FI notification failed
  }
  if (eventTypeUpper === 'DATA_REQUESTED' || eventTypeUpper === 'FI_NOTIFICATION_RECEIVED') {
    return 'pending'; // Blue - data operation in progress
  }

  // For webhook events, use status field (SUCCESS/FAILED)
  if (eventTypeUpper.startsWith('WEBHOOK_')) {
    if (statusUpper === 'SUCCESS' || statusUpper === 'SUCCESSFUL') {
      return 'success';
    }
    if (statusUpper === 'FAILED' || statusUpper === 'FAILURE' || statusUpper === 'ERROR') {
      return 'error';
    }
    return 'pending';
  }

  // Fallback: Use status for unknown event types
  if (statusUpper === 'FAILED' || statusUpper === 'FAILURE' || statusUpper === 'ERROR') {
    return 'error';
  }
  if (statusUpper === 'SUCCESS' || statusUpper === 'SUCCESSFUL' || statusUpper === 'ACTIVE' ||
      statusUpper === 'COMPLETE' || statusUpper === 'COMPLETED') {
    return 'success';
  }

  // Default to blue (pending)
  return 'pending';
}

/**
 * Transform v2 API event to UI timeline event
 */
function transformEvent(event: TimelineEventAPI): TimelineEvent {
  const config = getEventDisplayConfig(event.event_type, event.status);

  // Parse timestamp with validation
  const timestamp = event.time_stamp ? new Date(event.time_stamp) : null;
  const isValidDate = timestamp && !isNaN(timestamp.getTime());

  let formattedTimestamp = '—';
  if (isValidDate) {
    const timeString = timestamp.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    const dateString = timestamp.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    formattedTimestamp = `${timeString}, ${dateString}`;
  }

  // Build enhanced description from details
  let description = config.description;

  // Enhance description with context from details
  if (event.details.fiu_name) {
    description = `${event.details.fiu_name} ${description.toLowerCase()}`;
  } else if (event.details.fip_name) {
    const fiDescription = event.details.fi_n_description as string | undefined;
    // Only use fiDescription if it's a meaningful value (not just the literal word "description")
    const isValidDescription = fiDescription &&
      fiDescription.toLowerCase() !== 'description' &&
      fiDescription.trim().length > 0;
    description = isValidDescription ? fiDescription : `${event.details.fip_name}: ${description}`;
  } else if (event.details.webhook_event_type) {
    description = `Webhook: ${event.details.webhook_event_type}`;
  }

  // Determine badge color based on event_type (matching Angular behavior)
  // Consent events use event_type, webhook events use status
  const statusBadgeType = getStatusBadgeType(event.status, event.event_type);

  return {
    id: event.event_id,
    timestamp: formattedTimestamp,
    status: {
      type: statusBadgeType,
      label: event.status,
    },
    title: config.title,
    description,
    action: event.txn_id ? {
      type: 'copy' as const,
      label: `Txn ID: ${event.txn_id}`,
      value: event.txn_id,
    } : undefined,
    metadata: {
      ...event.details,
      _parsedTimestamp: isValidDate ? timestamp : null, // Store Date object for TAT calculations
    },
    rawData: event as any,
  };
}

/**
 * Transform v2 API consent timeline to UI timeline
 */
export function transformConsentTimeline(
  consentTimeline: ConsentTimelineAPIResponse['consent_timeline']
): TimelineEvent[] {
  return consentTimeline.events.map(transformEvent);
}

/**
 * Transform API data sessions to UI format
 */
export function transformDataSessions(sessions: DataSessionAPI[]): TransformedDataSession[] {
  return sessions.map((session) => {
    // Map session status to UI variant
    let statusVariant: 'success' | 'warning' | 'error' | 'info' = 'info';

    const statusUpper = session.session_status.toUpperCase();
    if (statusUpper === 'COMPLETE' || statusUpper === 'COMPLETED' || statusUpper === 'READY' || statusUpper === 'DELIVERED') {
      statusVariant = 'success';
    } else if (statusUpper === 'FAILED' || statusUpper === 'TIMEOUT') {
      statusVariant = 'error';
    } else if (statusUpper === 'PENDING') {
      statusVariant = 'info';
    }

    // Transform events
    const timeline = session.events.map(transformEvent);

    return {
      sessionId: session.session_id,
      status: session.session_status,
      statusVariant,
      accounts: session.accounts.map(acc => ({
        fipId: acc.fip_id,
        accountNumber: acc.masked_account_number,
        accountType: acc.account_type,
        fiType: acc.fi_type,
        linkRefNumber: acc.link_reference_number,
      })),
      timeline,
    };
  });
}

/**
 * Calculate overall TAT (Turn-Around Time)
 * Returns null for duration if timestamps are invalid or missing
 */
export function calculateOverallTAT(events: TimelineEvent[]): {
  duration: string | null;
  startTime: Date | null;
  endTime: Date | null;
} {
  // Return null if no events
  if (!events || events.length === 0) {
    return { duration: null, startTime: null, endTime: null };
  }

  // Extract Date objects from metadata
  const startTime = events[0]?.metadata?._parsedTimestamp as Date;
  const endTime = events[events.length - 1]?.metadata?._parsedTimestamp as Date;

  // Return null if either timestamp is missing or invalid
  if (!startTime || !endTime || !(startTime instanceof Date) || !(endTime instanceof Date)) {
    return { duration: null, startTime: null, endTime: null };
  }

  // Check if dates are valid (not NaN)
  if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
    return { duration: null, startTime: null, endTime: null };
  }

  const durationMs = endTime.getTime() - startTime.getTime();

  // Return null if duration is negative or invalid
  if (durationMs < 0 || isNaN(durationMs)) {
    return { duration: null, startTime: null, endTime: null };
  }

  // Format duration
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // Additional safety check for NaN in calculations
  if (isNaN(seconds) || isNaN(minutes) || isNaN(hours) || isNaN(days)) {
    return { duration: null, startTime, endTime };
  }

  let duration = '';
  if (days > 0) duration = `${days}d ${hours % 24}h`;
  else if (hours > 0) duration = `${hours}h ${minutes % 60}m`;
  else if (minutes > 0) duration = `${minutes}m ${seconds % 60}s`;
  else duration = `${seconds}s`;

  return {
    duration,
    startTime,
    endTime,
  };
}
